import React, { useState, useEffect, useRef } from "react";
import Barcode from "react-barcode";
import { useReactToPrint } from "react-to-print";
import { privateAxios } from "../utils/axios";

const generateBaseCode = (prefix) => {
  const random = Math.random().toString(36).substring(2, 10).toUpperCase().padEnd(8, "0");
  const p = (prefix || "JPT").substring(0, 3).toUpperCase().padEnd(3, "X");
  return `${p}${random}`;
};

function PrintableBarcodes({ items }) {
  const allBarcodes = items.flatMap((item) =>
    item.barcodeValues.map((barcodeValue) => ({ barcodeValue, item }))
  );

  const pages = [];
  for (let i = 0; i < allBarcodes.length; i += 3) {
    pages.push(allBarcodes.slice(i, i + 3));
  }

  return (
    <div style={{ fontFamily: "sans-serif", margin: 0, padding: 0 }}>
      <style>{`@page { size: 75mm 100mm; margin: 0; }`}</style>
      {pages.map((page, pageIndex) => (
        <div
          key={pageIndex}
          style={{
            width: "75mm",
            height: "100mm",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-evenly",
            alignItems: "center",
            padding: "2mm",
            boxSizing: "border-box",
            pageBreakInside: "avoid",
            breakInside: "avoid",
            pageBreakAfter: pageIndex < pages.length - 1 ? "always" : "avoid",
            breakAfter: pageIndex < pages.length - 1 ? "page" : "avoid",
          }}
        >
          {page.map(({ barcodeValue, item }, idx) => (
            <div
              key={barcodeValue}
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                borderBottom: idx < page.length - 1 ? "0.5px dashed #d1d5db" : "none",
                padding: "2mm 1mm",
              }}
            >
              <p
                style={{
                  fontSize: "9px",
                  fontWeight: "600",
                  color: "#374151",
                  margin: "0 0 1mm 0",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.productName}
              </p>
              <p style={{ fontSize: "8px", color: "#6b7280", margin: "0 0 1.5mm 0" }}>
                {item.category}
              </p>
              <Barcode
                value={barcodeValue}
                width={1.2}
                height={35}
                fontSize={9}
                margin={2}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function BarcodeGenerator() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [productName, setProductName] = useState("");
  const [category, setCategory] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [prefix, setPrefix] = useState("");

  const [generatedItems, setGeneratedItems] = useState([]);
  const [errors, setErrors] = useState({});

  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          privateAxios.get("/products/all"),
          privateAxios.get("/category"),
        ]);
        setProducts(prodRes.data?.data || prodRes.data || []);
        setCategories(catRes.data?.data || catRes.data || []);
      } catch (err) {
        console.error("Failed to fetch products/categories", err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const validate = () => {
    const e = {};
    if (!productName.trim()) e.productName = "Product name is required";
    if (!category.trim()) e.category = "Category is required";
    if (!quantity || quantity < 1) e.quantity = "Quantity must be at least 1";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});

    const qty = parseInt(quantity, 10);
    const newItem = {
      id: Date.now(),
      productName: productName.trim(),
      category: category.trim(),
      quantity: qty,
      barcodeValues: (() => { const base = generateBaseCode(prefix); return Array.from({ length: qty }, (_, i) => `${base}${String(i + 1).padStart(2, "0")}`); })(),
      createdAt: new Date().toLocaleString(),
    };

    setGeneratedItems((prev) => [newItem, ...prev]);
    setProductName("");
    setCategory("");
    setQuantity(1);
    setPrefix("");
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Barcodes",
  });

  const handleDelete = (id) => {
    setGeneratedItems((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Barcode Generator</h1>
          <p className="text-sm text-slate-500 mt-1">
            Generate unique barcodes for product identification
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-semibold text-slate-700 mb-4">
                Generate Barcode
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Product Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    Product Name
                  </label>
                  <input
                    type="text"
                    list="product-list"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Enter or select product"
                    className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                      errors.productName ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  <datalist id="product-list">
                    {products.map((p, i) => (
                      <option
                        key={i}
                        value={p.name || p.productName || p.title || p}
                      />
                    ))}
                  </datalist>
                  {errors.productName && (
                    <p className="text-xs text-red-500 mt-1">{errors.productName}</p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition bg-white ${
                      errors.category ? "border-red-400" : "border-gray-200"
                    }`}
                  >
                    <option value="">
                      {loadingData ? "Loading..." : "Select category"}
                    </option>
                    {categories.map((cat, i) => (
                      <option
                        key={i}
                        value={cat.name || cat.categoryName || cat.title || cat}
                      >
                        {cat.name || cat.categoryName || cat.title || cat}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-xs text-red-500 mt-1">{errors.category}</p>
                  )}
                </div>

                {/* Prefix */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    Prefix <span className="text-slate-400 font-normal">(optional, 3 letters of your product — default JPT)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={3}
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value.replace(/[^a-zA-Z]/g, ""))}
                    placeholder="JPT"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition uppercase"
                  />
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                      errors.quantity ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.quantity && (
                    <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl transition text-sm shadow-sm shadow-blue-600/30"
                >
                  Generate Barcode
                </button>
              </form>
            </div>
          </div>

          {/* Generated list */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-slate-700">
                  Generated Barcodes
                  {generatedItems.length > 0 && (
                    <span className="ml-2 text-xs font-normal bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                      {generatedItems.length}
                    </span>
                  )}
                </h2>
                {generatedItems.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setGeneratedItems([]);
                        setProductName("");
                        setCategory("");
                        setQuantity(1);
                        setErrors({});
                      }}
                      className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium px-3 py-2 rounded-xl transition"
                      title="Clear all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reset
                    </button>
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Print All
                    </button>
                  </div>
                )}
              </div>

              {generatedItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <svg className="w-12 h-12 mb-3 opacity-40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zm0 9.75c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zm9.75-9.75c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zm0 9.75h.75v.75h-.75v-.75zm9.75-9.75h.75v.75h-.75v-.75z" />
                  </svg>
                  <p className="text-sm">No barcodes generated yet</p>
                  <p className="text-xs mt-1">Fill the form and click Generate</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                  {generatedItems.map((item) =>
                    item.barcodeValues.map((barcodeValue, i) => {
                      return (
                        <div
                          key={`${item.id}-${i}`}
                          className="border border-gray-100 rounded-xl p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center hover:border-blue-100 transition"
                        >
                          {/* Barcode preview */}
                          <div className="shrink-0 bg-white border border-gray-200 rounded-lg p-2 flex flex-col items-center">
                            <Barcode
                              value={barcodeValue}
                              width={1.5}
                              height={50}
                              fontSize={9}
                              margin={4}
                            />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800 text-sm truncate">
                              {item.productName}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Category: <span className="text-slate-700">{item.category}</span>
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              #{i + 1} of {item.quantity}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5 font-mono">
                              {barcodeValue}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">{item.createdAt}</p>
                          </div>

                          {/* Delete (only show on first barcode of each item) */}
                          {i === 0 && (
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition"
                              title="Remove all barcodes for this product"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden print area */}
      <div className="hidden">
        <div ref={printRef}>
          <PrintableBarcodes items={generatedItems} />
        </div>
      </div>
    </div>
  );
}
